import { useState } from "react";
import { ICON_BASE_PATH, BANK_LIST_PATH } from "@/app/utils/config";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import MaskedAccountNumber from "@/app/utils/helpers/MaskedAccountNumber";
import { ChevronDown, ChevronUp, Download } from "lucide-react";
import StatusBadge from "@/app/components/common/badge/StatusBadge";
import { gxPaymentsBulkTransactionDetailDownloadApi } from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
import type { BulkPaymentReviewOverviewProps } from "../BulkUpload";
import useCopyToClipboards from "@/app/hooks/useCopyToClipboards";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import { t } from "i18next";

const formatSalaryMonth = (raw: string | null | undefined): string => {
  if (raw?.length !== 6) return raw ?? "N/A";
  const month = Number.parseInt(raw.substring(0, 2), 10) - 1;
  const year = raw.substring(2);
  const date = new Date(Number(year), month, 1);
  return date.toLocaleString("en-US", { month: "short", year: "numeric" });
};

const formatBankRoutingCode = (
  code: string | undefined | null = "",
  bank: string | undefined | null = "",
): string => {
  if (code && bank) return `${code} (${bank})`;
  return code || bank || "N/A";
};

const BulkPaymentReviewOverview = ({
  reviewSummary,
  payrollReviewSummary,
  isPayroll = false,
  shouldShowTransactionSummary = false,
  logoName,
  bankName,
  debitAccountNo,
}: BulkPaymentReviewOverviewProps) => {
  const [isBulkDetailsOpen, setIsBulkDetailsOpen] = useState(true);
  const [isBatchDetailsOpen, setIsBatchDetailsOpen] = useState(true);
  const [isFileMetadataOpen, setIsFileMetadataOpen] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const { copyToClipboard, isCopiedClip } = useCopyToClipboards();

  const usePayrollData = isPayroll && payrollReviewSummary;

  const gxJobChnNo = usePayrollData
    ? (payrollReviewSummary?.gxJobChnNo ?? "")
    : (reviewSummary?.gxJobChnNo ?? "");

  const gxJobProcessid = usePayrollData
    ? (payrollReviewSummary?.gxJobProcessid ?? null)
    : (reviewSummary?.gxJobProcessid ?? null);

  const totalAmount = usePayrollData
    ? (payrollReviewSummary?.totalAmount ?? 0)
    : (reviewSummary?.totalAmount ?? 0);

  const totalTxnCount = usePayrollData
    ? (payrollReviewSummary?.totalTxnCount ?? 0)
    : (reviewSummary?.totalTxnCount ?? 0);

  const fileName = usePayrollData
    ? (payrollReviewSummary?.fileName ?? "")
    : (reviewSummary?.fileName ?? "");

  const validTxnCount = usePayrollData
    ? (payrollReviewSummary?.validTxnCount ?? 0)
    : (reviewSummary?.validTxnCount ?? 0);

  const invalidTxnCount = usePayrollData
    ? (payrollReviewSummary?.invalidTxnCount ?? 0)
    : (reviewSummary?.invalidTxnCount ?? 0);

  const bulktype = usePayrollData
    ? (payrollReviewSummary?.bulktype ?? "")
    : (reviewSummary?.bulktype ?? "");

  const fileFormat = usePayrollData
    ? (payrollReviewSummary?.fileFormat ?? "")
    : (reviewSummary?.fileFormat ?? "");

  const crtdDt = usePayrollData
    ? (payrollReviewSummary?.jobStartDate ?? null)
    : (reviewSummary?.crtdDt ?? null);

  let debitAccountName = "N/A";
  let finalDebitAccountNo = debitAccountNo || "N/A";
  let finalBankName = bankName || "N/A";
  let finalLogoName = logoName || "";

  if (usePayrollData) {
    const payrollEntity =
      payrollReviewSummary?.wpsBulkFileTransactionsHDREntity;
    if (payrollEntity) {
      debitAccountName = payrollEntity.debitAccountName || "N/A";
      if (!debitAccountNo)
        finalDebitAccountNo = payrollEntity.debitAccountNo || "N/A";
      if (!bankName) finalBankName = payrollEntity.debitBankName || "N/A";
    }
  } else if (reviewSummary?.gxPayBulkFileTransaction?.[0]) {
    const firstTransaction = reviewSummary.gxPayBulkFileTransaction[0];
    debitAccountName = firstTransaction.debitAccountName || "N/A";
    if (!debitAccountNo)
      finalDebitAccountNo = firstTransaction.debitAccountNo || "N/A";
    if (!bankName) finalBankName = firstTransaction.debitBankName || "N/A";
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    } catch {
      return dateString;
    }
  };

  const downloadExcel = async () => {
    if (!gxJobProcessid) return;

    try {
      setIsDownloading(true);
      const response = await gxPaymentsBulkTransactionDetailDownloadApi({
        gxJobProcessId: gxJobProcessid,
      });

      if (response.status === 1 && response.data) {
        const {
          fileName: dlFileName,
          fileData,
          fileExtensionType,
        } = response.data;

        const binaryString = atob(fileData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.codePointAt(i) ?? 0;
        }

        let mimeType = "application/octet-stream";
        if (fileExtensionType === ".xlsx") {
          mimeType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        } else if (fileExtensionType === ".xls") {
          mimeType = "application/vnd.ms-excel";
        } else if (fileExtensionType === ".csv") {
          mimeType = "text/csv";
        }

        const blob = new Blob([bytes], { type: mimeType });
        const url = globalThis.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          dlFileName || `bulk_file_${gxJobChnNo}${fileExtensionType}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        globalThis.URL.revokeObjectURL(url);
      }
    } catch {
    } finally {
      setIsDownloading(false);
    }
  };

  const currencyCode = usePayrollData
    ? payrollReviewSummary?.wpsBulkFileTransactionsHDREntity?.baseCurrency
    : reviewSummary?.gxPayBulkFileTransaction?.[0]?.baseCurrency ||
      reviewSummary?.gxPayBulkFileTransaction?.[0]?.debitCurrency;

  const hdrEntity = payrollReviewSummary?.wpsBulkFileTransactionsHDREntity;
  const agentId = hdrEntity?.gxwpsBulkFileTransactions?.[0]?.agentId || "";

  if (!reviewSummary && !payrollReviewSummary) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 gx-p14-b">
          <NoDataFound
            className="gx-no-data"
            message={t("no_data_available")}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="vw-100">
        <div className="gx-bpr-overview-h">
          <h2 className="gx-h5-b">{t("overview")}</h2>

          <div className="gx-bpr-ov-ta-sec gx-bpr-overview">
            <div className="gx-bpr-ov-ta-p">
              <p className="gx-p16-m">{t("total_amount")}</p>
            </div>
            <div className="gx-h2-24-b gx-bpr-ov-ta-p-2">
              {gxCurrencyFormat(totalAmount, currencyCode).symbol}{" "}
              {gxCurrencyFormat(totalAmount, currencyCode).integer}
              {gxCurrencyFormat(totalAmount, currencyCode).decimal}
            </div>
          </div>

          <div className="gx-bpr-debit-form">
            <div className="gx-bpr-debit-form-p">
              <span className="gx-p10">{t("debit_from")}</span>
            </div>
            <div className="gx-bpr-debit-form-in-sec">
              <div className="gx-bpr-debit-form-in-img">
                {finalLogoName ? (
                  <img
                    src={`${BANK_LIST_PATH}${finalLogoName}`}
                    alt={finalBankName}
                    onError={(e) => {
                      e.currentTarget.src = `${ICON_BASE_PATH}bank-icon.svg`;
                    }}
                  />
                ) : (
                  <img src={`${ICON_BASE_PATH}bank-icon.svg`} alt="Bank logo" />
                )}
              </div>
              <div className="gx-bpr-debit-form-in-flex">
                <div className="gx-p12-b">{bankName}</div>
                <div className="gx-p10 gx-bpr-debit-form-in-pw-sec">
                  <MaskedAccountNumber
                    accountNumber={finalDebitAccountNo}
                    showToggle
                    toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {shouldShowTransactionSummary && (
          <div className="gx-bpr-transaction-summary">
            <div className="gx-bpr-ts-row">
              <span className="gx-p12-r">{t("total_transactions")}</span>
              <span className="gx-p12-b">{totalTxnCount}</span>
            </div>
            <div className="gx-bpr-ts-row">
              <span className="gx-p12-r">
                {t("total")} {usePayrollData ? "Processed" : "Paid"}
              </span>
              <span className="gx-p12-b gx-bpr-ts-paid">{validTxnCount}</span>
            </div>
            <div className="gx-bpr-ts-row">
              <span className="gx-p12-r">{t("total_pending")}</span>
              <span className="gx-p12-b gx-bpr-ts-pending">
                {invalidTxnCount}
              </span>
            </div>
            <div className="gx-bpr-ts-row">
              <span className="gx-p12-r">{t("total_failed")}</span>
              <span className="gx-p12-b gx-bpr-ts-failed">0</span>
            </div>
          </div>
        )}

        {usePayrollData && (
          <div>
            <button
              type="button"
              className={`gx-bpr-bfd-heading mt-4 mb-4 ${isBatchDetailsOpen ? "gx-bpr-bfd-heading--active" : ""}`}
              onClick={() => setIsBatchDetailsOpen((prev) => !prev)}
            >
              <h3 className="gx-p14-b">{t("batch_details")}</h3>
              {isBatchDetailsOpen ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronUp size={20} />
              )}
            </button>

            {isBatchDetailsOpen && (
              <div className="gx-p12-r gx-bpr-bfd-open-sp-sec mb-4">
                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("agent_code")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">{agentId || "N/A"}</p>
                    <button
                      type="button"
                      className="cursor-pointer"
                      onClick={() => copyToClipboard(agentId, "agentId")}
                    >
                      <img
                        src={`${ICON_BASE_PATH}${isCopiedClip("agentId") ? "green_tick.svg" : "copy-icon.svg"}`}
                        alt={
                          isCopiedClip("agentId")
                            ? t("copied_successfully")
                            : t("copy_to_clipboard")
                        }
                      />
                    </button>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("salary_month")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">
                      {formatSalaryMonth(hdrEntity?.salaryMonth)}
                    </p>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("employer_unique_id")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">
                      {hdrEntity?.employerUniqueId || "N/A"}
                    </p>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("bank_routing_code")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">
                      {formatBankRoutingCode(
                        hdrEntity?.paymentTypeCode,
                        hdrEntity?.debitBankName,
                      )}
                    </p>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("employer_ref_no")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">
                      {hdrEntity?.employerReference || "N/A"}
                    </p>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("employees_count")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">{totalTxnCount}</p>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {usePayrollData && (
          <div>
            <button
              type="button"
              className={`gx-bpr-bfd-heading mt-4 mb-4 ${isFileMetadataOpen ? "gx-bpr-bfd-heading--active" : ""}`}
              onClick={() => setIsFileMetadataOpen((prev) => !prev)}
            >
              <h3 className="gx-p14-b">{t("file_metadata")}</h3>
              {isFileMetadataOpen ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronUp size={20} />
              )}
            </button>

            {isFileMetadataOpen && (
              <div className="gx-p12-r gx-bpr-bfd-open-sp-sec mb-4">
                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("creation_date")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">{formatDate(crtdDt)}</p>
                  </span>
                </div>
                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("creation_time")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">{formatTime(crtdDt)}</p>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <button
            type="button"
            className={`gx-bpr-bfd-heading ${isBulkDetailsOpen ? "gx-bpr-bfd-heading--active" : ""}`}
            onClick={() => setIsBulkDetailsOpen((prev) => !prev)}
          >
            <h3 className="gx-p14-b">{t("bulk_file_details")}</h3>
            {isBulkDetailsOpen ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </button>

          {isBulkDetailsOpen && (
            <>
              <div className="gx-p12-r gx-bpr-bfd-open-sp-sec">
                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("growx_reference_no")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">{gxJobChnNo}</p>
                    <button
                      className="cursor-pointer"
                      onClick={() => copyToClipboard(gxJobChnNo, "gxJobChnNo")}
                    >
                      <img
                        src={`${ICON_BASE_PATH}${isCopiedClip("gxJobChnNo") ? "green_tick.svg" : "copy-icon.svg"}`}
                        alt=""
                      />
                    </button>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("bulk_type")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">{bulktype}</p>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("file_format")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">{fileFormat || "N/A"}</p>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("uploaded_on")}</p>
                  </span>
                  <span className="gx-bpr-bfd-open-ref-no-p-r">
                    <p className="gx-p12-b">{formatDate(crtdDt)}</p>
                  </span>
                </div>

                <div className="gx-bpr-bfd-open-ref-no">
                  <span className="gx-bpr-bfd-open-ref-no-p">
                    <p className="gx-p12-r">{t("file_status")}</p>
                  </span>
                  <StatusBadge
                    module="bulk"
                    statusCode={
                      usePayrollData
                        ? payrollReviewSummary?.jobStatusCode
                        : reviewSummary?.jobStatusCode
                    }
                  />
                </div>
              </div>

              <div className="gx-bpr-bfd-open-uploaded-file-sec">
                <div className="gx-bpr-bfd-open-uploaded-file">
                  <p className="gx-p14-b">{t("uploaded_file")}</p>
                </div>
                <div className="gx-bpr-bfd-open-uploaded-file-in-sec">
                  <div className="gx-bpr-bfd-open-uploaded-file-icon-sec">
                    <div className="gx-bpr-bfd-open-uploaded-file-icon">
                      {bulktype === "PAYROLL" ? (
                        <img src={`${ICON_BASE_PATH}fileIcon.svg`} alt="File" />
                      ) : (
                        <img
                          src={`${ICON_BASE_PATH}microsoft_excel-logo.wine.svg`}
                          alt="File"
                        />
                      )}
                    </div>
                    <div>
                      <div className="gx-bpr-bfd-open-uf-icon-xl">
                        <p className="gx-p12-m">{fileName}</p>
                      </div>
                      <div className="gx-bpr-bfd-open-uf-icon-t-mb">
                        <p className="gx-p10-m">
                          {totalTxnCount} {t("transactions")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="gx-bpr-bfd-open-uf-download-excel">
                    <button
                      onClick={downloadExcel}
                      disabled={isDownloading}
                      style={{ opacity: isDownloading ? 0.6 : 1 }}
                    >
                      <div>
                        <Download className="gx-bprcl-download-icon" />
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkPaymentReviewOverview;
